import * as React from "react";

import type { OrgLlmConnectionRecord } from "@/shared/domain/orgs";
import {
  useDeleteOrgLlmConnection,
  useOrgLlmProvider,
  useOrgLlmProviderTemplates,
} from "@/shared/domain/orgs/hooks";
import {
  ConfirmDialog,
  FormSection,
  RobotoButton,
  Spinner,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import { ConnectionDialog } from "./ConnectionDialog";
import { ConnectionListItem } from "./ConnectionListItem";
import styles from "./OrgLlmConnections.module.css";

interface OrgLlmConnectionsSectionProps {
  orgId: string;
  /** Org admins of this org and Roboto admins manage connections; everyone else reads. */
  canEdit: boolean;
  /** Connections can be added or changed only on a premium org; any org may delete one. */
  isPremium: boolean;
}

/** Which dialog is open: adding, editing a connection, or none. */
type EditTarget = { connection?: OrgLlmConnectionRecord } | null;

/**
 * The org's own LLM connections (its credentials to an LLM provider), which
 * models on its list may run on. Premium only; a downgraded org still sees
 * and can delete the ones it has.
 */
export function OrgLlmConnectionsSection({
  orgId,
  canEdit,
  isPremium,
}: OrgLlmConnectionsSectionProps) {
  const providerQuery = useOrgLlmProvider(orgId);
  const templatesQuery = useOrgLlmProviderTemplates(orgId);
  const deleteMutation = useDeleteOrgLlmConnection();
  const [editTarget, setEditTarget] = React.useState<EditTarget>(null);
  const [deleting, setDeleting] = React.useState<OrgLlmConnectionRecord | null>(
    null,
  );

  const connections = providerQuery.data?.connections;
  const canManage = canEdit && isPremium;

  const actions = canManage ? (
    <RobotoButton size="sm" onClick={() => setEditTarget({})}>
      Add connection
    </RobotoButton>
  ) : undefined;

  const closeDelete = () => {
    setDeleting(null);
    deleteMutation.reset();
  };

  return (
    <FormSection
      title="LLM connections"
      hint="Your organization's own credentials to an LLM provider. Models on them are billed by your provider, not through AI credits."
      actions={actions}
    >
      {!isPremium && (
        <p className={styles.note}>
          Own LLM connections are a premium feature. Upgrade to premium to add
          connections and chat on your own models.
        </p>
      )}
      {providerQuery.isLoading && (
        <Spinner aria-label="Loading LLM connections" />
      )}
      {providerQuery.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(providerQuery.error)}
        </p>
      )}
      {connections !== undefined && connections.length === 0 && isPremium && (
        <p className={styles.note}>No connections yet.</p>
      )}
      {connections !== undefined && connections.length > 0 && (
        <ul className={styles.list} aria-label="Connections">
          {connections.map((connection) => (
            <ConnectionListItem
              key={connection.connection_id}
              orgId={orgId}
              connection={connection}
              canVerify={canManage}
              onEdit={
                canManage ? () => setEditTarget({ connection }) : undefined
              }
              onDelete={canEdit ? () => setDeleting(connection) : undefined}
            />
          ))}
        </ul>
      )}

      <ConnectionDialog
        orgId={orgId}
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
          }
        }}
        connection={editTarget?.connection}
        templates={templatesQuery.data?.connections ?? {}}
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeDelete();
          }
        }}
        title={`Delete connection “${deleting?.label ?? ""}”?`}
        description="Models on your list can no longer use it. Its API key secret is kept."
        confirmLabel="Delete"
        destructive
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(
              { orgId, connectionId: deleting.connection_id },
              { onSuccess: closeDelete },
            );
          }
        }}
      >
        {deleteMutation.isError && (
          <p className={styles.error} role="alert">
            {userFacingErrorMessage(deleteMutation.error)}
          </p>
        )}
      </ConfirmDialog>
    </FormSection>
  );
}
